import { Sdk_AssetType, Sdk_AudioFormat, Sdk_MediaTrack_Role, Sdk_StreamType, } from '@zattoo/taf-protobuf/protobuf/sdk';
import { PlayerOutput_AdType, PlayerOutput_PlaybackStateChanged_State, PlayerOutput_TimeshiftAvailability, } from '@zattoo/taf-protobuf/protobuf/player_output';
import { AdType, MediaTrackRole, PlaybackState, SeekResultType, StreamType, } from '@zattoo/playback-sdk';
import { AssetType, AudioFormat, } from '@zattoo/playback-sdk-react-native-zapi';
import { TimeshiftStatus } from '@zattoo/zapi/lib/enums';
import { Output, Output_Log_Level, Output_Log_Source, } from '@zattoo/taf-protobuf/protobuf/action';
export const convertAssetTypeFromTaf = (assetType) => {
    switch (assetType) {
        case Sdk_AssetType.LIVE:
            return AssetType.LIVE;
        case Sdk_AssetType.RECORDING:
            return AssetType.RECORDING;
        case Sdk_AssetType.REPLAY:
            return AssetType.REPLAY;
        case Sdk_AssetType.REGISTERED_TIMESHIFT:
            return AssetType.REGISTERED_TIMESHIFT;
        case Sdk_AssetType.VOD:
            return AssetType.VOD;
        case Sdk_AssetType.URL:
            return AssetType.URL;
        case Sdk_AssetType.VOD_TRAILER:
            return AssetType.VOD_TRAILER;
        default:
            return AssetType.URL;
    }
};
export const convertPreferredAudioLanguage = (preferredAudioLanguage) => {
    switch (preferredAudioLanguage) {
        case Sdk_AudioFormat.DOLBY:
            return AudioFormat.DOLBY;
        case Sdk_AudioFormat.STEREO:
            return AudioFormat.STEREO;
        default:
            return undefined;
    }
};
export const convertAssetInfoFromTaf = (assetInfo) => {
    return {
        ...assetInfo,
        assetType: convertAssetTypeFromTaf(assetInfo?.assetType),
    };
};
export const convertStreamTypeFromTaf = (streamType) => {
    switch (streamType) {
        case Sdk_StreamType.DASH:
            return StreamType.DASH;
        case Sdk_StreamType.DASH_PLAYREADY:
            return StreamType.DASH_PLAYREADY;
        case Sdk_StreamType.DASH_WIDEVINE:
            return StreamType.DASH_WIDEVINE;
        case Sdk_StreamType.HLS7:
            return StreamType.HLS7;
        case Sdk_StreamType.HLS7_FAIRPLAY:
            return StreamType.HLS7_FAIRPLAY;
        default:
            return StreamType.DASH;
    }
};
export const convertStreamTypeToTaf = (streamType) => {
    switch (streamType) {
        case StreamType.DASH:
            return Sdk_StreamType.DASH;
        case StreamType.DASH_PLAYREADY:
            return Sdk_StreamType.DASH_PLAYREADY;
        case StreamType.DASH_WIDEVINE:
            return Sdk_StreamType.DASH_WIDEVINE;
        case StreamType.HLS7:
            return Sdk_StreamType.HLS7;
        case StreamType.HLS7_FAIRPLAY:
            return Sdk_StreamType.HLS7_FAIRPLAY;
        default:
            throw new Error(`Unknown stream type ${streamType}`);
    }
};
export const playOptions = (options) => {
    return {
        ...options,
        preferredAudioFormat: convertPreferredAudioLanguage(options.preferredAudioFormat),
        adsInfo: options.adsInfo,
        assetInfo: convertAssetInfoFromTaf(options.assetInfo),
    };
};
export const zapiPlayOptions = (options) => {
    return {
        ...options,
        preferredAudioFormat: convertPreferredAudioLanguage(options.preferredAudioFormat),
        gt12ChannelInfo: options.gt12ChannelInfo ? {
            ...options.gt12ChannelInfo,
        } : undefined,
    };
};
const convertSource = (source) => {
    switch (source) {
        case Output_Log_Source.PLAYER:
            return 'PLAYER';
        case Output_Log_Source.PLAYER_ADAPTER:
            return 'PLAYER_ADAPTER';
        case Output_Log_Source.TAF:
            return 'TAF';
        default:
            return 'UNKNOWN';
    }
};
const convertLevel = (level) => {
    switch (level) {
        case Output_Log_Level.INFO:
            return 'INFO';
        case Output_Log_Level.DEBUG:
            return 'DEBUG';
        case Output_Log_Level.WARN:
            return 'WARN';
        case Output_Log_Level.ERROR:
            return 'ERROR';
        default:
            return 'UNKNOWN';
    }
};
export const createLog = (source, level, ...args) => {
    console.log(`${convertSource(source)}::${convertLevel(level)}: ${JSON.stringify(args)}`);
    return Output.create({
        log: {
            source,
            level,
            message: args.map((arg) => JSON.stringify(arg)),
        },
    });
};
export const convertAudioFormat = (audioFormat) => {
    if (audioFormat === AudioFormat.DOLBY) {
        return Sdk_AudioFormat.DOLBY;
    }
    if (audioFormat === AudioFormat.STEREO) {
        return Sdk_AudioFormat.STEREO;
    }
    return undefined;
};
export const sdkMediaTrackRolesAsTaf = (roles) => {
    if (roles === undefined) {
        return undefined;
    }
    return {
        roles: roles.map((role) => {
            switch (role) {
                case MediaTrackRole.MAIN:
                    return Sdk_MediaTrack_Role.MAIN;
                case MediaTrackRole.ALTERNATE:
                    return Sdk_MediaTrack_Role.ALTERNATE;
                case MediaTrackRole.CAPTION:
                    return Sdk_MediaTrack_Role.CAPTION;
                case MediaTrackRole.TRANSCRIBES_SPOKEN_DIALOG:
                    return Sdk_MediaTrack_Role.TRANSCRIBES_SPOKEN_DIALOG;
                case MediaTrackRole.DESCRIBES_MUSIC_AND_SOUND:
                    return Sdk_MediaTrack_Role.DESCRIBES_MUSIC_AND_SOUND;
                default:
                    throw new Error(`Unknown role type ${role}`);
            }
        }),
    };
};
export const tafMediaTrackRolesAsSdk = (wrapper) => {
    if (wrapper === undefined) {
        return undefined;
    }
    return wrapper.roles.map((role) => {
        switch (role) {
            case Sdk_MediaTrack_Role.MAIN:
                return MediaTrackRole.MAIN;
            case Sdk_MediaTrack_Role.ALTERNATE:
                return MediaTrackRole.ALTERNATE;
            case Sdk_MediaTrack_Role.CAPTION:
                return MediaTrackRole.CAPTION;
            case Sdk_MediaTrack_Role.TRANSCRIBES_SPOKEN_DIALOG:
                return MediaTrackRole.TRANSCRIBES_SPOKEN_DIALOG;
            case Sdk_MediaTrack_Role.DESCRIBES_MUSIC_AND_SOUND:
                return MediaTrackRole.DESCRIBES_MUSIC_AND_SOUND;
            default:
                throw new Error(`Unknown role type ${role}`);
        }
    });
};
export const convertMediaTrack = (mediaTrack) => {
    return {
        id: mediaTrack.id,
        codec: mediaTrack.codec,
        locale: mediaTrack.locale,
        audioFormat: convertAudioFormat(mediaTrack.audioFormat),
        rolesWrapper: sdkMediaTrackRolesAsTaf(mediaTrack.roles),
    };
};
export const convertAssetType = (assetType) => {
    switch (assetType) {
        case AssetType.LIVE:
            return Sdk_AssetType.LIVE;
        case AssetType.VOD:
            return Sdk_AssetType.VOD;
        case AssetType.REPLAY:
            return Sdk_AssetType.REPLAY;
        case AssetType.RECORDING:
            return Sdk_AssetType.RECORDING;
        case AssetType.REGISTERED_TIMESHIFT:
            return Sdk_AssetType.REGISTERED_TIMESHIFT;
        case AssetType.URL:
            return Sdk_AssetType.URL;
        case AssetType.VOD_TRAILER:
            return Sdk_AssetType.VOD_TRAILER;
        default:
            throw new Error(`Unknown asset type ${assetType}`);
    }
};
export const convertStreamTypeInformation = (streamTypeInformation) => {
    return {
        type: convertStreamTypeToTaf(streamTypeInformation.type),
        maxSecurityLevel: streamTypeInformation.maxSecurityLevel,
        version: streamTypeInformation.version,
    };
};
export const convertDeviceCapabilities = (capabilities) => {
    const result = {
        streamTypeInformation: capabilities.streamTypeInformation.map(convertStreamTypeInformation),
        hdcpLevel: capabilities.hdcpLevel,
        audioCodecs: capabilities.audioCodecs ?? [],
        videoCodecs: capabilities.videoCodecs ?? [],
        subtitlesCodecs: capabilities.subtitlesCodecs ?? [],
    };
    return result;
};
export const convertTimeshiftStatus = (sdkTimeshiftStatus) => {
    switch (sdkTimeshiftStatus) {
        case TimeshiftStatus.AVAILABLE:
            return PlayerOutput_TimeshiftAvailability.AVAILABLE;
        case TimeshiftStatus.ELIGIBLE:
            return PlayerOutput_TimeshiftAvailability.ELIGIBLE;
        case TimeshiftStatus.SUBSCRIBABLE:
            return PlayerOutput_TimeshiftAvailability.SUBSCRIBABLE;
        case TimeshiftStatus.UNAVAILABLE:
            return PlayerOutput_TimeshiftAvailability.UNAVAILABLE;
        default:
            return undefined;
    }
};
// TODO: https://zattoo2.atlassian.net/browse/PLY-1484
export const convertMediaZapi = (media) => {
    return {
        url: media.url,
        licenseUrl: media.licenseUrl ?? undefined,
        prePadding: media.prePadding,
        postPadding: media.postPadding,
        assetType: convertAssetType(media.assetType),
        dynamic: media.dynamic,
        timeshiftAvailability: convertTimeshiftStatus(media.timeshiftAvailability ?? undefined),
    };
};
export const convertMedia = (media) => {
    return {
        url: media.url,
        licenseUrl: media.licenseUrl ?? undefined,
        prePadding: undefined,
        postPadding: undefined,
        assetType: convertAssetType(media.assetType),
        dynamic: false,
        timeshiftAvailability: undefined,
    };
};
export const convertPlaybackState = (state) => {
    switch (state) {
        case PlaybackState.PLAYING:
            return PlayerOutput_PlaybackStateChanged_State.PLAYING;
        case PlaybackState.BUFFERING:
            return PlayerOutput_PlaybackStateChanged_State.BUFFERING;
        case PlaybackState.PAUSED:
            return PlayerOutput_PlaybackStateChanged_State.PAUSED;
        case PlaybackState.STOPPED:
            return PlayerOutput_PlaybackStateChanged_State.STOPPED;
        default:
            throw new Error(`Unknown state ${state}`);
    }
};
export const convertSeekResult = (seekResult) => {
    switch (seekResult.type) {
        case SeekResultType.ALLOWED:
            return {
                allowed: {
                    position: seekResult.seekPosition,
                },
            };
        case SeekResultType.FORWARD_SEEKING_NOT_ALLOWED:
            return {
                forwardSeekingNotAllowed: {},
            };
        case SeekResultType.GT12_ALLOWED:
            return {
                gt12Allowed: {
                    position: seekResult.seekPosition,
                },
            };
        case SeekResultType.GT12_SEEK_TO_ADBREAK:
            return {
                gt12SeekToAdBreak: {
                    position: seekResult.seekPosition,
                },
            };
        case SeekResultType.GT12_SEEK_TO_AND_DISPLAY_ADBREAK:
            return {
                gt12SeekToAndDisplayAdBreak: {
                    position: seekResult.seekPosition,
                    adBreak: seekResult.vastUrl,
                },
            };
        case SeekResultType.GT12_FORBIDDEN:
            return {
                gt12Forbidden: {},
            };
        case SeekResultType.STREAM_CHANGE:
            return {
                streamChange: {},
            };
        default:
            throw new Error(`Unknown seekResult ${seekResult}`);
    }
};
export const convertLatencyMetadata = (metadata) => {
    return {
        recorderStart: metadata.recorderStart ?? undefined,
        recorderEnd: metadata.recorderEnd ?? undefined,
        transcoderStart: metadata.transcoderStart ?? undefined,
        transcoderEnd: metadata.transcoderEnd ?? undefined,
        packagerEnd: metadata.packagerEnd ?? undefined,
        downloadEnd: metadata.downloadEnd ?? undefined,
        time: metadata.time ?? undefined,
    };
};
const convertAdType = (type) => {
    switch (type) {
        case AdType.PREROLL:
            return PlayerOutput_AdType.PREROLL;
        case AdType.POSTROLL:
            return PlayerOutput_AdType.POSTROLL;
        case AdType.MIDROLL:
            return PlayerOutput_AdType.MIDROLL;
        case AdType.START:
            return PlayerOutput_AdType.START;
        case AdType.FAST_FORWARD:
            return PlayerOutput_AdType.FAST_FORWARD;
        case AdType.PAUSE:
            return PlayerOutput_AdType.PAUSE;
        case AdType.UNKNOWN:
            return PlayerOutput_AdType.UNKNOWN;
        default:
            return PlayerOutput_AdType.UNKNOWN;
    }
};
export const convertAdInfo = (info) => {
    return {
        adId: info.adId,
        adTitle: info.adTitle,
        adDescription: info.adDescription,
        adSystem: info.adSystem,
        contentType: info.contentType,
        duration: info.duration,
        width: info.width,
        height: info.height,
        vastMediaWidth: info.vastMediaWidth,
        vastMediaHeight: info.vastMediaHeight,
        vastMediaBitrate: info.vastMediaBitrate,
        linear: info.linear,
        skippable: info.skippable,
        skipTimeOffset: info.skipTimeOffset,
        adPodInfo: info.adPodInfo,
        traffickingParameters: info.traffickingParameters,
        creativeAdId: info.creativeAdId,
        universalAdIds: info.universalAdIds,
    };
};
export const convertAdData = (event) => {
    return {
        adType: convertAdType(event.adType),
        adInfo: convertAdInfo(event.adInfo),
    };
};
export const convertAdTimeData = (data) => {
    return {
        adType: convertAdType(data.adType),
        adInfo: convertAdInfo(data.adInfo),
        position: data.position,
    };
};
export const convertAdBreakData = (data) => {
    return {
        adType: convertAdType(data.adType),
        isChannelAd: data.isChannelAd ?? undefined,
        maxDuration: data.maxDuration ?? undefined,
    };
};
export const convertAdMetadataData = (data) => {
    return {
        adType: convertAdType(data.adType),
        cuePoints: data.cuePoints,
    };
};
export const convertAdPositionChangedData = (data) => {
    return {
        adType: convertAdType(data.adType),
        position: data.position,
        duration: data.duration,
    };
};
export const convertAdErrorData = (data) => {
    return {
        adType: convertAdType(data.adType),
        error: {
            // TODO
            // adId: data.adId,
            code: data.error.code,
            message: data.error.message,
        },
    };
};
export const convertAdPauseData = (data) => {
    return {
        adUrl: data.url,
        adWidth: data.width,
        adHeight: data.height,
    };
};
